import { useEffect, useState } from 'react'
import { useAlert } from 'react-alert'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import type { ApiGetType } from '../../../../api'
import { deleteApi, getApi, putApi } from '../../../../api'
import { LICENSE_STATUS } from '../../../../config/constants'
import endpoints from '../../../../config/endpoints'
import { useTableLoader } from '../../../../hooks/useTableLoader'
import type { License } from '../../../../types/license'
import { PACKAGE_LABELS } from '../../../../types/license'
import ConfirmationDialog from '../../../../ui/ConfirmationDialog/ConfirmationDialog'
import type { FormValues } from '../../../../ui/Form/Form.types'
import { useLocationPagination } from '../../../../ui/Pagination'
import { StatusBadge } from '../../../../ui/StatusBadge'
import type { Column } from '../../../../ui/Table'
import { Table } from '../../../../ui/Table'
import { formatDate } from '../../../../utils/date'

import { mapFormValuesToDraftLicense } from './CreateLicense/CreateLicense.utils'
import { CreateLicense } from './CreateLicense'
import type { TenantLicensesProps } from './TenantLicenses.types'
import { UpdateLicense } from './UpdateLicense'

function TenantLicenses(props: TenantLicensesProps) {
  const { tenant } = props
  const { t } = useTranslation()
  const alert = useAlert()
  const { id } = useParams()

  const [isEditFormOpen, setIsEditFormOpen] = useState(false)
  const [pagination, setPagination] = useLocationPagination()
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null)
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false)

  const handleOpenEditForm = (license: License) => {
    setSelectedLicense(license)
    setIsEditFormOpen(true)
  }

  const handleCloseEditForm = () => {
    setSelectedLicense(null)
    setIsEditFormOpen(false)
  }

  const handleOpenConfirmationDialog = (license: License) => {
    setSelectedLicense(license)
    setIsConfirmationDialogOpen(true)
  }

  const handleCloseConfirmationDialog = () => {
    setSelectedLicense(null)
    setIsConfirmationDialogOpen(false)
  }

  const handleEditFormSubmit = (values: FormValues) => {
    putApi({
      endpoint: endpoints.license(id, selectedLicense?.id),
      data: mapFormValuesToDraftLicense(values),
    })
      .then(() => {
        handleCloseEditForm()
        alert.success(t('Tenants.Profile.Licenses.Form.Alert.EditSuccess'))
        loadInitial({}, pagination)
      })
      .catch((error) => {
        alert.error(error.message)
      })
  }

  const handleDeleteLicense = (license: License) => {
    deleteApi({
      endpoint: endpoints.license(id, license.id),
    })
      .then(() => {
        handleCloseConfirmationDialog()
        alert.success(t('Tenants.Profile.Licenses.Form.Alert.DeleteSuccess'))
        loadInitial({}, pagination)
      })
      .catch((error) => {
        alert.error(error.message)
      })
  }

  const {
    items: licenses,
    loading,
    loadInitial,
    initial,
  } = useTableLoader<License, Partial<ApiGetType>>({
    getApi: async (api) => {
      const { ...params } = api.params ?? {}

      const results = await getApi<License[]>({
        ...api,
        endpoint: endpoints.license(id),
        params: {
          ...params,
        },
      })

      return {
        count: results?.length ?? 0,
        results: results ?? [],
        next: null,
        previous: null,
      }
    },
    setPagination,
  })

  useEffect(() => {
    loadInitial({}, pagination)
  }, [loadInitial]) // eslint-disable-line

  const handleLicenseCreated = () => {
    loadInitial({}, pagination)
  }

  const columns: Column<License>[] = [
    {
      header: 'Tenants.Profile.Licenses.Fields.Name',
      render: (license) => PACKAGE_LABELS[license.package] || license.package,
    },
    {
      header: 'Tenants.Profile.Licenses.Fields.StartDate',
      render: (license) => formatDate(license.startDate),
    },
    {
      header: 'Tenants.Profile.Licenses.Fields.EndDate',
      render: (license) => formatDate(license.endDate),
    },
    {
      header: 'Tenants.Profile.Licenses.Fields.Status',
      render: (license) => <StatusBadge status={license.status} />,
    },
    {
      header: 'General.Actions',
      accessor: undefined,
      render: (license: License) => {
        if (!tenant) {
          return null
        }

        const isActive = license.status === LICENSE_STATUS.ACTIVE
        const isInactive = license.status === LICENSE_STATUS.INACTIVE
        const isPlanned = license.status === LICENSE_STATUS.PLANNED
        const canDeleteLicense = isPlanned && !isActive && !isInactive

        return (
          <div className="flex gap-2">
            <button
              className="px-3 py-1 rounded text-sm font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => handleOpenEditForm(license)}
            >
              {t('General.Edit')}
            </button>
            <button
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                canDeleteLicense
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              disabled={!canDeleteLicense}
              onClick={() => handleOpenConfirmationDialog(license)}
            >
              {t('General.Delete')}
            </button>
          </div>
        )
      },
    },
  ]

  return (
    <div className="p-6">
      <CreateLicense onLicenseCreated={handleLicenseCreated} />

      <UpdateLicense
        isEditFormOpen={isEditFormOpen}
        initialValues={selectedLicense}
        onClose={handleCloseEditForm}
        onSubmit={handleEditFormSubmit}
      />

      <Table
        data={licenses}
        initial={initial}
        isLoading={loading}
        columns={columns}
        emptyMessage="Tenants.Profile.Licenses.Empty"
        pagination={pagination}
        setPagination={setPagination}
        title="Tenants.Profile.Licenses.TableTitleInternal"
      />

      {isConfirmationDialogOpen && selectedLicense && (
        <ConfirmationDialog
          title={t('Tenants.Profile.Licenses.Form.Dialog.Title')}
          message={t('Tenants.Profile.Licenses.Form.Dialog.Message')}
          confirmButtonText={t('General.Delete')}
          cancelButtonText={t('General.Cancel')}
          open={isConfirmationDialogOpen}
          hideModal={handleCloseConfirmationDialog}
          onConfirm={() => handleDeleteLicense(selectedLicense)}
          onCancel={handleCloseConfirmationDialog}
        />
      )}
    </div>
  )
}

export default TenantLicenses
